import { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { Trophy, Target, Crosshair, Clock, RefreshCw, Download, ExternalLink, Crown, Search, Loader2, Link2, Mail } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';

// Lazy load TournamentMode to reduce initial bundle size
const TournamentMode = lazy(() => import('./TournamentMode').then(m => ({ default: m.TournamentMode })));
interface Player {
  weste: string;
  name: string;
  team: string;
  score: string;
  shots: string;
  accurancy: string;
  playerurl: string;
}

interface Team {
  team: string;
  score: string;
  rank: string;
}

interface GameData {
  game: string;
  timestamp: string;
  teams: Team[];
  players: Player[];
  file?: string;
}

interface HitData {
  player: string;
  targets: { [key: string]: number };
}

const teamColors = {
  Yellow: { bg: 'bg-team-yellow', text: 'text-team-yellow', border: 'border-team-yellow', glow: 'glow-team-yellow' },
  Red: { bg: 'bg-team-red', text: 'text-team-red', border: 'border-team-red', glow: 'glow-team-red' },
  Blue: { bg: 'bg-team-blue', text: 'text-team-blue', border: 'border-team-blue', glow: 'glow-team-blue' },
  Ocean: { bg: 'bg-team-blue', text: 'text-team-blue', border: 'border-team-blue', glow: 'glow-team-blue' },
  Green: { bg: 'bg-team-green', text: 'text-team-green', border: 'border-team-green', glow: 'glow-team-green' },
  Purple: { bg: 'bg-team-purple', text: 'text-team-purple', border: 'border-team-purple', glow: 'glow-team-purple' },
};

const defaultTeamColors = { bg: 'bg-muted', text: 'text-foreground', border: 'border-border', glow: 'glow-primary' };

const getTeamColors = (teamName: string) => {
  return teamColors[teamName as keyof typeof teamColors] || defaultTeamColors;
};

const getRankIcon = (rank: number) => {
  switch (rank) {
    case 1:
      return <Trophy className="w-6 h-6 text-neon-yellow" />;
    case 2:
      return <Trophy className="w-5 h-5 text-gray-400" />;
    case 3:
      return <Trophy className="w-5 h-5 text-orange-400" />;
    default:
      return <span className="w-6 h-6 flex items-center justify-center text-lg font-bold text-muted-foreground">#{rank}</span>;
  }
};

// Keep all player data including playerurl for linking
const sanitizeGameData = (data: GameData): GameData => {
  return data;
};


export const HighscoreBoard = () => {
  const [gameData, setGameData] = useState<GameData | null>(null);
  const [hitData, setHitData] = useState<HitData[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [savedHighscores, setSavedHighscores] = useState<any[]>([]);
  const [selectedHighscoreId, setSelectedHighscoreId] = useState<string>('auto');
  const [hasManualSelection, setHasManualSelection] = useState(false);
  const [showTournamentMode, setShowTournamentMode] = useState(false);
  const [manualGameNumber, setManualGameNumber] = useState('');
  const [loadingManualGame, setLoadingManualGame] = useState(false);
  const [showCTA, setShowCTA] = useState(false);
  
  const { toast } = useToast();

  // Show CTA button only after scrolling down
  useEffect(() => {
    const handleScroll = () => {
      const scrollThreshold = 200; // Show after scrolling 200px
      setShowCTA(window.scrollY > scrollThreshold);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchDetailData = async (filename: string, players?: Player[]) => {
    try {
      // Add cache buster to prevent browser caching
      const cacheBuster = `?_=${Date.now()}`;
      const gameUrl = `https://lasertag-arena-siegen.de/highscores/games/${filename}${cacheBuster}`;
      console.log('Fetching detail data from:', gameUrl);

      // Use edge function to avoid CORS issues
      const { data: responseData, error } = await supabase.functions.invoke('fetch-game-data', {
        body: { url: gameUrl }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(`Failed to fetch game data: ${error.message}`);
      }

      if (!responseData || !responseData.data) {
        throw new Error('No data returned from edge function');
      }

      const raw = responseData.data;
      console.log('Detail file raw content:', raw.substring(0, 500));
      console.log('Full detail file length:', raw.length);

      // Parse the custom game file format
      console.log('Parsing custom game file format...');
      const processedHitData = parseGameFile(raw, players);
      if (processedHitData.length > 0) {
        console.log('Successfully parsed hit data:', processedHitData);
        setHitData(processedHitData);
      } else {
        console.log('No hit data found in game file');
        // Ensure we still show an empty table for all players
        if (players && players.length > 0) {
          setHitData(players.map(p => ({ player: p.name, targets: {} })));
        }
      }
    } catch (error) {
      console.error('Failed to load detail data:', error);
      setHitData(null);
    }
  };

  const parseGameFile = (content: string, playersParam?: Player[]): HitData[] => {
    try {
      console.log('Parsing game file content...');
      const hitData: HitData[] = [];

      // Split content into sections
      const sections = content.split('#').filter(s => s.trim());
      console.log('Found sections:', sections.length);

      // Player mapping and hit matrix
      const playerMap = new Map<string, string>(); // weste -> name
      const playerUrlMap = new Map<string, string>(); // weste -> playerurl
      const hitMatrix: { [shooter: string]: { [target: string]: number } } = {};
      const packOrderWestes: string[] = []; // Ordered list of player vest numbers IN THE ORDER THEY APPEAR IN PACK{}

      // First pass: Parse PACK{} and PACKX{} entries to establish position mapping and URLs
      sections.forEach(section => {
        const trimmed = section.trim();
        if (trimmed.startsWith('PACK{')) {
          const packData = trimmed.match(/PACK\{([^}]+)\}/);
          if (packData) {
            const parts = packData[1].split(',');
            if (parts.length >= 2) {
              const weste = parts[0].trim();
              const name = parts[1].trim();
              playerMap.set(weste, name);
              packOrderWestes.push(weste);
              console.log('Found PACK player at position', packOrderWestes.length - 1, ':', weste, name);
            }
          }
        }
        // Parse PACKX{} to extract playerurl
        // Format: PACKX{weste,score,shots,hits,?,rank,URL,...}
        if (trimmed.startsWith('PACKX{')) {
          const packxData = trimmed.match(/PACKX\{([^}]+)\}/);
          if (packxData) {
            const parts = packxData[1].split(',');
            if (parts.length >= 7) {
              const weste = parts[0].trim();
              const url = parts[6].trim();
              if (url && url.startsWith('http')) {
                playerUrlMap.set(weste, url);
                console.log('Found playerurl for weste', weste, ':', url);
              }
            }
          }
        }
      });

      console.log('PACK order westes:', packOrderWestes);

      // Second pass: Parse HITS{} using the PACK order
      sections.forEach(section => {
        const trimmed = section.trim();

        // Parse HITS lines: HITS{shooter, h0, h1, h2, ...}
        // where h0 is hits on player at PACK position 0, h1 on player at PACK position 1, etc.
        if (trimmed.startsWith('HITS{') || trimmed.includes('HITS{')) {
          const hitsMatches = trimmed.match(/HITS\{([^}]+)\}/g);
          if (hitsMatches) {
            hitsMatches.forEach(hit => {
              const data = hit.match(/HITS\{([^}]+)\}/);
              if (data) {
                const parts = data[1].split(',').map(s => s.trim());
                if (parts.length >= 2) {
                  const shooter = parts[0];
                  if (!hitMatrix[shooter]) hitMatrix[shooter] = {};
                  
                  // The positions after shooter correspond to PACK order
                  for (let i = 1; i < parts.length; i++) {
                    const val = parseInt(parts[i], 10) || 0;
                    // i-1 because first number is shooter, so i=1 maps to index 0 of packOrderWestes
                    const targetIndex = i - 1;
                    if (targetIndex < packOrderWestes.length) {
                      const targetWeste = packOrderWestes[targetIndex];
                      if (val > 0) {
                        hitMatrix[shooter][targetWeste] = val;
                      }
                    }
                  }
                }
              }
            });
          }
        }
      });

      console.log('Player map:', playerMap);
      console.log('PACK order westes:', packOrderWestes);
      console.log('Hit matrix:', hitMatrix);

      // Build rows for all players and add playerurl to player objects
      const playersList = playersParam ?? gameData?.players ?? [];
      if (playersList.length > 0) {
        // Add playerurl to existing player objects
        playersList.forEach(p => {
          const url = playerUrlMap.get(p.weste);
          if (url && !p.playerurl) {
            p.playerurl = url;
            console.log('Added playerurl to player', p.name, ':', url);
          }
          hitData.push({
            player: p.name,
            targets: hitMatrix[p.weste] || {}
          });
        });
      } else {
        // Fallback to whatever we parsed if players list is unavailable
        packOrderWestes.forEach(weste => {
          const name = playerMap.get(weste) || `Weste ${weste}`;
          hitData.push({
            player: name,
            targets: hitMatrix[weste] || {}
          });
        });
      }

      console.log('Final hit data:', hitData);
      return hitData;
    } catch (error) {
      console.error('Error parsing game file:', error);
      return [];
    }
  };

  const fetchGameByNumber = async (gameNumber: string) => {
    const paddedNumber = gameNumber.padStart(4, '0');
    const apiUrl = `https://lasertag-arena-siegen.de/highscores/assets/php/import.php?game=${paddedNumber}&_=${Date.now()}`;
    
    console.log('Fetching specific game from API:', apiUrl);

    const { data: responseData, error } = await supabase.functions.invoke('fetch-game-data', {
      body: { url: apiUrl }
    });

    if (error) {
      throw new Error(`Failed to fetch game data: ${error.message}`);
    }

    if (!responseData || !responseData.data) {
      throw new Error('No data returned from edge function');
    }

    const raw = responseData.data;
    let data: GameData | null = null;
    
    try {
      data = JSON.parse(raw);
    } catch {
      const start = raw.indexOf('{');
      const end = raw.lastIndexOf('}');
      if (start !== -1 && end !== -1 && end > start) {
        data = JSON.parse(raw.slice(start, end + 1));
      } else {
        throw new Error('Antwort enthält kein parsebares JSON');
      }
    }

    if (!data?.players || !data?.teams) {
      throw new Error('Spiel nicht gefunden oder ungültiges Format');
    }

    return data;
  };

  const handleManualGameFetch = async () => {
    if (!manualGameNumber.trim()) return;
    
    const gameNum = manualGameNumber.trim();
    if (!/^\d+$/.test(gameNum)) {
      toast({
        title: "Ungültige Spielnummer",
        description: "Bitte geben Sie nur Zahlen ein.",
        variant: "destructive",
      });
      return;
    }

    setLoadingManualGame(true);
    try {
      const data = await fetchGameByNumber(gameNum);
      
      setGameData(data);
      setLastUpdate(new Date());
      
      // Save to database
      await saveHighscore(data);
      
      // Load detail data if file is available
      if (data.file) {
        fetchDetailData(data.file, data.players);
      }
      
      toast({
        title: "Spiel geladen!",
        description: `Spiel #${data.game} wurde erfolgreich abgerufen und gespeichert.`,
      });
      
      setManualGameNumber('');
    } catch (error) {
      console.error('Error fetching game:', error);
      toast({
        title: "Fehler beim Laden",
        description: error instanceof Error ? error.message : "Das Spiel konnte nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoadingManualGame(false);
    }
  };

  const fetchData = async () => {
    try {
      // Add cache buster to prevent browser caching
      const cacheBuster = `?_=${Date.now()}`;
      const apiUrl = `https://lasertag-arena-siegen.de/highscores/assets/php/import.php${cacheBuster}`;
      
      console.log('Fetching latest game data from API:', apiUrl);

      // Use edge function to avoid CORS issues
      const { data: responseData, error } = await supabase.functions.invoke('fetch-game-data', {
        body: { url: apiUrl }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(`Failed to fetch game data: ${error.message}`);
      }

      if (!responseData || !responseData.data) {
        throw new Error('No data returned from edge function');
      }

      const raw = responseData.data;
      console.log('Raw API response:', raw.substring(0, 200) + '...');

      let data: GameData | null = null;
      try {
        // Try direct JSON first
        data = JSON.parse(raw);
      } catch {
        // Fallback: extract JSON from potential HTML wrapper
        const start = raw.indexOf('{');
        const end = raw.lastIndexOf('}');
        if (start !== -1 && end !== -1 && end > start) {
          const jsonString = raw.slice(start, end + 1);
          console.log('Extracted JSON:', jsonString.substring(0, 200) + '...');
          data = JSON.parse(jsonString);
        } else {
          throw new Error('Antwort enthält kein parsebares JSON');
        }
      }

      if (!data?.players || !data?.teams) {
        throw new Error('Unerwartetes Datenformat');
      }

      console.log('Successfully parsed data:', { 
        game: data.game, 
        teams: data.teams.length, 
        players: data.players.length 
      });

      // Only save if it's a different game than currently displayed
      const isNewGame = !gameData || gameData.game !== data.game;
      
      setGameData(data);
      setLastUpdate(new Date());
      setLoading(false);
      
      // Save to database only if it's a new game (reduces rate limit hits)
      if (isNewGame) {
        await saveHighscore(data);
      }
      
      // Load detail data if file is available
      if (data.file) {
        fetchDetailData(data.file, data.players);
      }
    } catch (error) {
      console.error('Failed to fetch game data:', error);

      // If live data fails, try to load the latest saved highscore
      try {
        const { data, error } = await supabase
          .from('highscores')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!error && data) {
          console.log('No live data available, loading latest saved highscore');
          const rawGameData = data.game_data as unknown as GameData;
          const sanitizedData = sanitizeGameData(rawGameData);
          setGameData(sanitizedData);
          setLastUpdate(new Date(data.timestamp));
          
          // Load detail data if file is available
          if (sanitizedData.file) {
            fetchDetailData(sanitizedData.file, sanitizedData.players);
          }
        }
      } catch (dbError) {
        console.error('Failed to load fallback data:', dbError);
      }
      
      setLoading(false);
    }
  };

  const saveHighscore = async (data: GameData) => {
    // Helper to detect rate limit errors
    const isRateLimitError = (str: string) => 
      str.includes('Rate limit') || 
      str.includes('429') ||
      str.includes('rate limit') ||
      str.includes('Rate limit exceeded');

    try {
      // Silent save attempt - rate limiting is expected and handled gracefully
      
      // Call the secure edge function instead of direct database insert
      const response = await supabase.functions.invoke('save-highscore', {
        body: {
          game_data: data,
          game_id: data.game,
        },
      });

      const { data: result, error } = response;

      if (error) {
        // Check all possible error formats for rate limit
        const errorStr = JSON.stringify(error) + (error.message || '') + (error.context?.body || '');
        
        if (isRateLimitError(errorStr)) {
          // Rate limiting is expected - just log info and return silently
          console.info('Rate limit active for game', data.game, '- this is expected behavior');
          return;
        }
        
        console.error('Error calling save-highscore function:', error);
        toast({
          title: "Fehler beim Speichern",
          description: "Das Highscore konnte nicht gespeichert werden.",
          variant: "destructive",
        });
        return;
      }

      // Check if the result itself contains an error (e.g., from 429 response body)
      if (result?.error) {
        if (isRateLimitError(result.error)) {
          console.info('Rate limit active (via result) for game', data.game);
          return;
        }
        
        console.error('Failed to save highscore:', result.error);
        toast({
          title: "Fehler beim Speichern",
          description: result.error || "Das Highscore konnte nicht gespeichert werden.",
          variant: "destructive",
        });
        return;
      }

      if (!result?.success) {
        // No success but also no error - might be rate limited
        console.info('Save did not return success flag for game', data.game);
        return;
      }

      console.log('Highscore saved successfully via edge function');
      
      toast({
        title: "Highscore gespeichert!",
        description: `Spiel #${data.game} wurde erfolgreich gespeichert.`,
      });
      
      // Refresh saved highscores list
      loadSavedHighscores();
    } catch (error: any) {
      // Check if the caught error is a rate limit error in any form
      const errorStr = String(error) + (error?.message || '') + JSON.stringify(error || {});
      
      if (isRateLimitError(errorStr)) {
        console.info('Rate limit caught in exception for game', data.game);
        return;
      }
      
      console.error('Error in saveHighscore:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
    }
  };

  const backfillMissingGames = async (latestGameNumber: number) => {
    try {
      console.log('Backfill: Checking for missing games before', latestGameNumber);
      
      // Get existing game IDs from database
      const { data: existingGames, error } = await supabase
        .from('highscores')
        .select('game_id')
        .order('game_id', { ascending: false })
        .limit(50);
      
      if (error) {
        console.error('Backfill: Error fetching existing games:', error);
        return;
      }
      
      const existingGameIds = new Set((existingGames || []).map(g => g.game_id));
      console.log('Backfill: Existing games in DB:', Array.from(existingGameIds).join(', '));
      
      // Check games from latestGameNumber-1 down to latestGameNumber-10
      const gamesToFetch: number[] = [];
      for (let i = 1; i <= 10; i++) {
        const gameNum = latestGameNumber - i;
        if (gameNum > 0) {
          const paddedGameId = gameNum.toString().padStart(4, '0');
          if (!existingGameIds.has(paddedGameId)) {
            gamesToFetch.push(gameNum);
          }
        }
      }
      
      if (gamesToFetch.length === 0) {
        console.log('Backfill: All previous 10 games already exist in database');
        return;
      }
      
      console.log('Backfill: Missing games to fetch:', gamesToFetch.join(', '));
      
      // Fetch missing games sequentially to avoid overwhelming the API
      for (const gameNum of gamesToFetch) {
        try {
          console.log(`Backfill: Fetching game ${gameNum}...`);
          const data = await fetchGameByNumber(gameNum.toString());
          
          if (data?.players && data?.teams) {
            // Save to database silently (no toast)
            const { error: saveError } = await supabase.functions.invoke('save-highscore', {
              body: {
                game_data: data,
                game_id: data.game,
              },
            });
            
            if (saveError) {
              console.warn(`Backfill: Error saving game ${gameNum}:`, saveError);
            } else {
              console.log(`Backfill: Successfully saved game ${gameNum}`);
            }
          }
        } catch (fetchError) {
          console.warn(`Backfill: Error fetching game ${gameNum}:`, fetchError);
          // Continue with next game
        }
      }
      
      console.log('Backfill: Completed, refreshing highscore list');
      // Refresh the list after backfill
      await loadSavedHighscoresOnly();
    } catch (error) {
      console.warn('Backfill: Error in backfillMissingGames:', error);
    }
  };

  const loadSavedHighscoresOnly = async () => {
    try {
      // Load saved highscores from database
      console.log('loadSavedHighscoresOnly: Loading saved highscores from database...');
      const { data, error } = await supabase
        .from('highscores')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error loading saved highscores:', error);
        return;
      }

      // Group by game_id and keep the entry with the latest timestamp for each game
      const gameMap = new Map<string, any>();
      (data || []).forEach((row: any) => {
        if (!gameMap.has(row.game_id)) {
          gameMap.set(row.game_id, row);
        }
      });
      
      // Helper function to parse German date format "DD.MM.YYYY - HH:MM Uhr" from game_data.timestamp
      const parseGameDate = (gameData: any): Date => {
        try {
          const timestampStr = gameData?.timestamp;
          if (timestampStr && typeof timestampStr === 'string') {
            // Format: "28.12.2025 - 12:44 Uhr"
            const match = timestampStr.match(/(\d{2})\.(\d{2})\.(\d{4})\s*-\s*(\d{2}):(\d{2})/);
            if (match) {
              const [, day, month, year, hour, minute] = match;
              return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
            }
          }
        } catch (e) {
          console.error('Error parsing game date:', e);
        }
        // Fallback to database timestamp
        return new Date(0);
      };

      // Take unique games and sort by game_data.timestamp (actual game date, newest first)
      const uniqueList = Array.from(gameMap.values())
        .sort((a, b) => {
          // Primary sort: by actual game date from game_data.timestamp (newest first)
          const timeA = parseGameDate(a.game_data).getTime();
          const timeB = parseGameDate(b.game_data).getTime();
          if (timeA !== timeB) {
            return timeB - timeA;
          }
          // Secondary sort: by game_id (highest first) as tiebreaker
          return parseInt(b.game_id) - parseInt(a.game_id);
        })
        .slice(0, 10);
      console.log('loadSavedHighscoresOnly: Found unique games (sorted by date):', uniqueList.map(g => `${g.game_id}(${g.game_data?.timestamp || 'unknown'})`).join(', '));
      
      setSavedHighscores(uniqueList);
      
      // Auto-select the game with highest ID if no manual selection was made
      if (uniqueList.length > 0 && !hasManualSelection) {
        console.log('loadSavedHighscoresOnly: Auto-selecting game', uniqueList[0].game_id);
        setSelectedHighscoreId(uniqueList[0].id);
      }
    } catch (error) {
      console.error('Error in loadSavedHighscoresOnly:', error);
    }
  };

  const loadSavedHighscores = async (fetchLiveData: boolean = false) => {
    try {
      let latestGameNumber: number | null = null;
      
      // Only fetch fresh data if explicitly requested (initial load)
      if (fetchLiveData) {
        console.log('loadSavedHighscores: Fetching fresh data from live API...');
        await fetchData();
        
        // Get the latest game number from gameData after fetch
        if (gameData?.game) {
          latestGameNumber = parseInt(gameData.game);
        }
      }
      
      // Load saved highscores from database
      console.log('loadSavedHighscores: Loading saved highscores from database...');
      // Optimized query: sort in DB and limit results
      const { data, error } = await supabase
        .from('highscores')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(50); // Get more initially to handle duplicates

      if (error) {
        console.error('Error loading saved highscores:', error);
        return;
      }

      // Group by game_id and keep the entry with the latest timestamp for each game
      const gameMap = new Map<string, any>();
      (data || []).forEach((row: any) => {
        if (!gameMap.has(row.game_id)) {
          gameMap.set(row.game_id, row);
        }
      });
      
      // Helper function to parse German date format "DD.MM.YYYY - HH:MM Uhr" from game_data.timestamp
      const parseGameDate = (gameData: any): Date => {
        try {
          const timestampStr = gameData?.timestamp;
          if (timestampStr && typeof timestampStr === 'string') {
            // Format: "28.12.2025 - 12:44 Uhr"
            const match = timestampStr.match(/(\d{2})\.(\d{2})\.(\d{4})\s*-\s*(\d{2}):(\d{2})/);
            if (match) {
              const [, day, month, year, hour, minute] = match;
              return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
            }
          }
        } catch (e) {
          console.error('Error parsing game date:', e);
        }
        // Fallback to database timestamp
        return new Date(0);
      };

      // Take unique games and sort by game_data.timestamp (actual game date, newest first)
      const uniqueList = Array.from(gameMap.values())
        .sort((a, b) => {
          // Primary sort: by actual game date from game_data.timestamp (newest first)
          const timeA = parseGameDate(a.game_data).getTime();
          const timeB = parseGameDate(b.game_data).getTime();
          if (timeA !== timeB) {
            return timeB - timeA;
          }
          // Secondary sort: by game_id (highest first) as tiebreaker
          return parseInt(b.game_id) - parseInt(a.game_id);
        })
        .slice(0, 10);
      console.log('loadSavedHighscores: Found unique games (sorted by date):', uniqueList.map(g => `${g.game_id}(${g.game_data?.timestamp || 'unknown'})`).join(', '));
      
      setSavedHighscores(uniqueList);
      
      // Auto-select the game with highest ID if no manual selection was made
      if (uniqueList.length > 0 && !hasManualSelection) {
        console.log('loadSavedHighscores: Auto-selecting game', uniqueList[0].game_id);
        setSelectedHighscoreId(uniqueList[0].id);
        
        // If this is initial load, trigger backfill with the latest game number
        // Use setTimeout to defer backfill so it doesn't block initial page load
        if (fetchLiveData) {
          const highestGameNumber = parseInt(uniqueList[0].game_id);
          if (highestGameNumber > 0) {
            // Defer backfill to after initial render is complete (5 seconds delay)
            setTimeout(() => {
              backfillMissingGames(highestGameNumber);
            }, 5000);
          }
        }
      }
    } catch (error) {
      console.error('Error in loadSavedHighscores:', error);
    }
  };

  const loadHighscoreById = async (id: string) => {
    if (id === 'live') {
      fetchData();
      return;
    }
    
    if (id === 'auto') {
      // Auto mode: wait for loadSavedHighscores to set the correct ID
      return;
    }

    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('highscores')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error loading highscore:', error);
        setLoading(false);
        return;
      }

      const rawGameData = data.game_data as unknown as GameData;
      // Sanitize player data to remove sensitive URLs
      const gameData = sanitizeGameData(rawGameData);
      setGameData(gameData);
      setLastUpdate(new Date(data.timestamp));
      
      // Show main data immediately
      setLoading(false);

      // Reset hit data first
      setHitData(null);

      // Load detail data asynchronously without blocking UI
      if (gameData.file) {
        fetchDetailData(gameData.file, gameData.players).catch(err => {
          console.error('Error loading detail data:', err);
          // Create empty hit data structure on error
          setHitData(gameData.players.map(p => ({ player: p.name, targets: {} })));
        });
      } else {
        // If no file, create empty hit data structure
        setHitData(gameData.players.map(p => ({ player: p.name, targets: {} })));
      }
    } catch (error) {
      console.error('Error in loadHighscoreById:', error);
      setLoading(false);
    }
  };

  const fetchBase64Image = async (url: string): Promise<string | null> => {
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) return null;
      const blob = await res.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  const downloadPDF = async () => {
    if (!gameData) return;

    try {
      toast({
        title: "PDF wird erstellt...",
        description: "Bitte warten Sie einen Moment.",
      });

      // Get the Supabase project URL
      const { data: { session } } = await supabase.auth.getSession();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      // Prepare voucher image as Base64 (from public folder)
      const gutscheinBase64 = await fetchBase64Image('/gutschein.png');

      // Call the edge function directly to get binary data
      const response = await fetch(`${supabaseUrl}/functions/v1/generate-highscore-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({ 
          game_data: gameData,
          hit_data: hitData,
          gutschein_image_base64: gutscheinBase64 || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error('PDF generation failed');
      }

      // Get the blob from the response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `highscore-${gameData.game}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "PDF heruntergeladen!",
        description: "Das Highscore-PDF wurde erfolgreich heruntergeladen.",
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        title: "Fehler",
        description: "PDF konnte nicht erstellt werden.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    // Initial load: fetch live data
    loadSavedHighscores(true);
    
    // Auto-refresh every 30 seconds to check for new games in database only
    const interval = setInterval(() => {
      if (!hasManualSelection) {
        console.log('Auto-refresh triggered at:', new Date().toLocaleTimeString());
        loadSavedHighscores(false); // Don't fetch live data on auto-refresh
      }
    }, 30000);
    
    return () => {
      clearInterval(interval);
    };
  }, [hasManualSelection]);

  useEffect(() => {
    if (selectedHighscoreId !== 'auto') {
      loadHighscoreById(selectedHighscoreId);
    }
  }, [selectedHighscoreId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <RefreshCw className="w-8 h-8 animate-spin text-neon-cyan mx-auto" />
          <p className="text-xl text-neon-cyan">Lade Highscores...</p>
        </div>
      </div>
    );
  }

  if (!gameData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-xl text-muted-foreground">Keine Daten verfügbar</p>
        </div>
      </div>
    );
  }

  const sortedPlayers = [...gameData.players].sort((a, b) => parseInt(b.score) - parseInt(a.score));
  console.log('Total players to display:', sortedPlayers.length);
  console.log('All players:', sortedPlayers.map(p => p.name));

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `vor ${diffHours} Std.`;
    
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatGameNumber = (gameNumber: string | number) => {
    return String(gameNumber).padStart(3, '0');
  };

  if (showTournamentMode) {
    return (
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      }>
        <TournamentMode onClose={() => setShowTournamentMode(false)} />
      </Suspense>
    );
  }

  return (
    <>
      <div className="min-h-screen p-2 sm:p-4 space-y-4 sm:space-y-8 pb-20 sm:pb-24">
      {/* Highscore Selector */}
      <Card className="p-3 sm:p-4 border-2 border-primary/20 bg-card/50 backdrop-blur-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex-1">
            <h3 className="text-base sm:text-lg font-semibold">Highscore Auswahl</h3>
          </div>
          
          {/* Share Buttons */}
          <div className="flex items-center gap-1 sm:gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-[#1877F2] hover:bg-[#1877F2]/10"
              onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank', 'width=600,height=400')}
              title="Auf Facebook teilen"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-foreground/10"
              onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(`Check out my Lasertag Highscore! Spiel #${gameData?.game || ''}`)}`, '_blank', 'width=600,height=400')}
              title="Auf X teilen"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-[#25D366] hover:bg-[#25D366]/10"
              onClick={() => window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(`Check out my Lasertag Highscore! Spiel #${gameData?.game || ''} ${window.location.href}`)}`, '_blank')}
              title="Auf WhatsApp teilen"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-[#0A66C2] hover:bg-[#0A66C2]/10"
              onClick={() => window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}`, '_blank', 'width=600,height=400')}
              title="Auf LinkedIn teilen"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                toast({ title: "Link kopiert!", description: "Der Link wurde in die Zwischenablage kopiert." });
              }}
              title="Link kopieren"
            >
              <Link2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10"
              onClick={() => window.open(`mailto:?subject=${encodeURIComponent(`Lasertag Highscore - Spiel #${gameData?.game || ''}`)}&body=${encodeURIComponent(`Check out my Lasertag Highscore!\n\n${window.location.href}`)}`, '_self')}
              title="Per E-Mail teilen"
            >
              <Mail className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <Select 
              value={selectedHighscoreId} 
              onValueChange={(value) => {
                setSelectedHighscoreId(value);
                setHasManualSelection(true);
              }}
            >
              <SelectTrigger className="w-full sm:w-[250px] bg-background" aria-label="Highscore-Spiel auswählen">
                <SelectValue placeholder="Wähle einen Highscore" />
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                <SelectItem value="live">🔴 Live - Aktuelles Spiel</SelectItem>
                {savedHighscores.map((highscore) => {
                  const gameData = highscore.game_data as GameData;
                  return (
                    <SelectItem key={highscore.id} value={highscore.id}>
                      Spiel #{formatGameNumber(gameData.game)} - {gameData.timestamp}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Button
              onClick={downloadPDF}
              variant="outline"
              size="default"
              className="flex items-center gap-2 border-primary/20 hover:bg-primary/10 w-full sm:w-auto"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Als PDF</span>
              <span className="sm:hidden">PDF</span>
            </Button>
            <Button
              onClick={() => setShowTournamentMode(true)}
              variant="outline"
              size="default"
              className="flex items-center gap-2 border-neon-yellow/30 hover:bg-team-yellow text-neon-yellow w-full sm:w-auto"
            >
              <Crown className="w-4 h-4" />
              <span className="hidden sm:inline">Turnier-Modus</span>
              <span className="sm:hidden">Turnier</span>
            </Button>
          </div>
        </div>
        
        {/* Manual Game Fetch */}
        <div className="mt-4 pt-4 border-t border-border/50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Älteres Spiel abrufen:</span>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Input
                type="text"
                placeholder="z.B. 817"
                value={manualGameNumber}
                onChange={(e) => setManualGameNumber(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && handleManualGameFetch()}
                className="w-24 bg-background"
                maxLength={4}
              />
              <Button
                onClick={handleManualGameFetch}
                disabled={loadingManualGame || !manualGameNumber.trim()}
                variant="outline"
                size="default"
                className="flex items-center gap-2 border-primary/20 hover:bg-primary/10"
              >
                {loadingManualGame ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                <span>Laden</span>
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Team Rankings - Only show if more than one team */}
      {gameData.teams.length > 1 && (
        <Card className="p-3 sm:p-6 border-2 border-primary/20 bg-card/50 backdrop-blur-sm animate-slide-up">
          <h2 className="text-lg sm:text-2xl font-bold mb-4 sm:mb-6 text-center flex items-center justify-center space-x-2">
            <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-neon-yellow" />
            <span>Team Rankings</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {gameData.teams.map((team, index) => {
              const colors = getTeamColors(team.team);
              return (
                <Card 
                  key={team.team} 
                  className={`p-3 sm:p-4 border-2 ${colors.border} ${colors.bg} backdrop-blur-sm transition-all duration-300 hover:${colors.glow} animate-float`}
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  <div className="text-center space-y-1 sm:space-y-2">
                    <div className="flex items-center justify-center space-x-2">
                      {getRankIcon(parseInt(team.rank))}
                      <span className={`text-base sm:text-xl font-bold ${colors.text}`}>
                        Team {team.team}
                      </span>
                    </div>
                    <div className={`text-2xl sm:text-3xl font-bold ${colors.text}`}>
                      {parseInt(team.score).toLocaleString()}
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      Platz {team.rank}
                    </Badge>
                  </div>
                </Card>
              );
            })}
          </div>
        </Card>
      )}

      {/* Player Leaderboard */}
      <Card className="p-3 sm:p-6 border-2 border-primary/20 bg-card/50 backdrop-blur-sm animate-slide-up">
        <h2 className="text-lg sm:text-2xl font-bold mb-2 text-center flex items-center justify-center space-x-2">
          <Target className="w-5 h-5 sm:w-6 sm:h-6 text-neon-cyan" />
          <span>Spieler Rankings</span>
        </h2>
        <p className="text-xs sm:text-sm text-muted-foreground text-center mb-4 sm:mb-6 px-2">
          Mehr Statistiken? Einfach auf deinen Namen klicken und die Online-Scorekarte aktivieren.
        </p>
        <div className="space-y-3">
          {sortedPlayers.map((player, index) => {
            console.log(`Rendering player ${index + 1}:`, player.name, 'Score:', player.score, 'Team:', player.team, 'PlayerURL:', player.playerurl);
            const colors = getTeamColors(player.team);
            const rank = index + 1;
            
            const cardContent = (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                <div className="flex items-center space-x-2 sm:space-x-4 w-full sm:w-auto">
                  <div className="flex-shrink-0">
                    {getRankIcon(rank)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className={`text-base sm:text-xl font-bold ${colors.text} truncate`}>
                        {player.name}
                      </h3>
                      {player.playerurl && (
                        <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      )}
                    </div>
                    <div className="flex items-center space-x-2 text-xs sm:text-sm text-muted-foreground">
                      <Badge 
                        variant="outline" 
                        className={`${colors.border} ${colors.text} border-current text-xs`}
                      >
                        Team {player.team}
                      </Badge>
                      <span className="text-xs">Weste #{player.weste}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 sm:space-x-6 text-right w-full sm:w-auto justify-between sm:justify-end">
                  <div className="space-y-1">
                    <div className={`text-xl sm:text-2xl font-bold ${colors.text}`}>
                      {parseInt(player.score).toLocaleString()}
                    </div>
                    <div className="text-xs text-muted-foreground">Punkte</div>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center space-x-1 text-xs sm:text-sm">
                      <Crosshair className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span>{player.shots}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">Schüsse</div>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="text-xs sm:text-sm font-medium">
                      {player.accurancy}%
                    </div>
                    <div className="text-xs text-muted-foreground">Quote</div>
                  </div>
                </div>
              </div>
            );

            return player.playerurl ? (
              <a
                key={`${player.weste}-${player.name}`}
                href={player.playerurl}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Card 
                  className={`p-3 sm:p-4 border ${colors.border} ${colors.bg} backdrop-blur-sm transition-all duration-300 hover:${colors.glow} hover:scale-[1.01] sm:hover:scale-[1.02] animate-fade-in cursor-pointer`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {cardContent}
                </Card>
              </a>
            ) : (
              <Card 
                key={`${player.weste}-${player.name}`}
                className={`p-3 sm:p-4 border ${colors.border} ${colors.bg} backdrop-blur-sm transition-all duration-300 hover:${colors.glow} hover:scale-[1.01] sm:hover:scale-[1.02] animate-fade-in`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {cardContent}
              </Card>
            );
          })}
        </div>
      </Card>

      {/* Hit Matrix Table */}
      {hitData && hitData.length > 0 && (
        <Card className="p-3 sm:p-6 border-2 border-primary/20 bg-card/50 backdrop-blur-sm animate-slide-up">
          <h2 className="text-lg sm:text-2xl font-bold mb-4 sm:mb-6 text-center flex items-center justify-center space-x-2">
            <Crosshair className="w-5 h-5 sm:w-6 sm:h-6 text-neon-red" />
            <span>Treffertabelle</span>
          </h2>
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <table className="w-full border-collapse min-w-max">
              <thead>
                <tr>
                  <th className="border border-border p-1 sm:p-2 text-left font-bold text-xs sm:text-sm sticky left-0 bg-card z-10">Spieler</th>
                  {gameData?.players
                    .slice()
                    .sort((a, b) => parseInt(a.weste) - parseInt(b.weste))
                    .map((player) => (
                      <th key={player.weste} className="border border-border p-1 sm:p-2 text-center font-bold text-xs whitespace-nowrap">
                        <span className="hidden sm:inline">{player.name}</span>
                        <span className="sm:hidden">W{player.weste}</span>
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {hitData.map((shooter, index) => {
                  // Find the shooter's team
                  const shooterPlayer = gameData?.players.find(p => p.name === shooter.player);
                  const shooterColors = shooterPlayer 
                    ? (teamColors[shooterPlayer.team as keyof typeof teamColors] || {
                        bg: 'bg-muted/20', 
                        text: 'text-foreground', 
                        border: 'border-muted', 
                        glow: 'shadow-md'
                      })
                    : {
                        bg: 'bg-muted/20', 
                        text: 'text-foreground', 
                        border: 'border-muted', 
                        glow: 'shadow-md'
                      };
                  
                  return (
                    <tr key={index} className="hover:bg-muted/20">
                      <td className={`border border-border p-1 sm:p-2 font-medium text-xs sm:text-sm ${shooterColors.text} sticky left-0 bg-card z-10`}>
                        <span className="hidden sm:inline">{shooter.player}</span>
                        <span className="sm:hidden truncate block max-w-[80px]">{shooter.player.split(' ')[0]}</span>
                      </td>
                      {gameData?.players
                        .slice()
                        .sort((a, b) => parseInt(a.weste) - parseInt(b.weste))
                        .map((target) => {
                          const hits = shooter.targets[target.weste] || 0;
                          const colors = getTeamColors(target.team);
                          return (
                            <td 
                              key={target.weste} 
                              className={`border border-border p-1 sm:p-2 text-center text-xs sm:text-sm ${hits > 0 ? colors.text + ' font-bold' : 'text-muted-foreground'}`}
                            >
                              {hits > 0 ? hits : '-'}
                            </td>
                          );
                        })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Auto-refresh indicator */}
      <div className="text-center text-sm text-muted-foreground">
        <div className="flex items-center justify-center space-x-2">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Automatische Aktualisierung alle 30 Sekunden</span>
        </div>
      </div>
    </div>
    
    {/* Sticky CTA Button - appears on scroll */}
    <div className={`fixed bottom-0 left-0 right-0 p-2 sm:p-4 bg-gradient-to-t from-background via-background to-transparent pointer-events-none z-50 transition-all duration-300 ${showCTA ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
      <div className="max-w-7xl mx-auto pointer-events-auto">
        <a
          href="https://onlinebuchung.lasertag-arena-siegen.de"
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full"
        >
          <button className="w-full h-10 sm:h-12 px-4 sm:px-8 bg-gradient-cyber text-white font-bold text-sm sm:text-lg rounded-md shadow-lg hover:shadow-glow-primary transition-all duration-300 hover:scale-[1.01] sm:hover:scale-[1.02] animate-pulse-glow">
            <span className="hidden sm:inline">🎯 Buche jetzt dein nächstes Spiel!</span>
            <span className="sm:hidden">🎯 Jetzt buchen!</span>
          </button>
        </a>
      </div>
    </div>
  </>
  );
};